import { DOCUMENT } from '@angular/common';
import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  Input,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  AfterViewChecked,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FormControl, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MarkdownService } from 'ngx-markdown';
import {
  ZenAIDomain,
  ZenAIQueryRequest,
  ZenAIStreamEvent,
  ZenAIStreamEventType,
  ZenAIReasoningEvent,
  ZenAICompleteEvent,
  ZenAIErrorEvent,
} from 'src/app/models/api/zenAi.model';
import { ZenAiHttpService } from 'src/app/services/httpServices/zenAiHttpService/zen-ai-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { NotificationsService } from 'src/app/services/infra/notifications/notifications.service';
import { ZenAiMessage, ZenAiMessageStatus } from './zen-ai-message/zen-ai-message.component';

@Component({
  selector: 'app-zen-ai-assistant',
  templateUrl: './zen-ai-assistant.component.html',
  styleUrls: ['./zen-ai-assistant.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ZenAiAssistantComponent implements OnInit, OnDestroy, AfterViewChecked {
  @Input({ required: true }) domain!: ZenAIDomain;
  @ViewChild('messagesContainer', { read: ElementRef }) private _messagesContainer?: ElementRef<HTMLDivElement>;

  public isExpanded = false;
  public isLoading = false;
  public queryControl = new FormControl('', [Validators.required]);
  public readonly messages: ZenAiMessage[] = [];
  public sessionId: string | undefined;

  // Expose enum to template
  public readonly ZenAiMessageStatus = ZenAiMessageStatus;

  // Auto-scroll management
  private _shouldAutoScroll = true;
  private _isNearBottom = true;
  private _lastScrollHeight = 0;

  private readonly _destroy$ = new Subject<void>();
  private readonly _document = inject(DOCUMENT);
  private readonly _sanitizer = inject(DomSanitizer);
  private readonly _markdownService = inject(MarkdownService);
  private readonly _zenAiHttpService = inject(ZenAiHttpService);
  private readonly _logger = inject(LoggerService);
  private readonly _notificationsService = inject(NotificationsService);
  private readonly _cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this._addWelcomeMessage();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  ngAfterViewChecked(): void {
    this._scrollToBottomIfNeeded();
  }

  public toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded) {
      // Reset scroll state when expanding
      this._shouldAutoScroll = true;
      this._isNearBottom = true;
    }
  }

  public onMessagesScroll(event: Event): void {
    const element = event.target as HTMLDivElement;
    const threshold = 50; // pixels from bottom to consider "at bottom"
    const atBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

    // Update tracking flags
    this._isNearBottom = atBottom;

    // If user scrolled to bottom, resume auto-scrolling
    if (atBottom) {
      this._shouldAutoScroll = true;
    } else {
      // User scrolled up, disable auto-scroll
      this._shouldAutoScroll = false;
    }
  }

  public sendQuery(): void {
    const prompt = this.queryControl.value?.trim();
    if (!prompt || this.isLoading) {
      return;
    }

    this._addUserMessage(prompt);
    this.queryControl.setValue('');
    this.isLoading = true;

    const request = this._createQueryRequest(prompt);
    const streamingStartIndex = this.messages.length;

    this._zenAiHttpService
      .query(request)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (event) => {
          this._handleStreamEvent(event, streamingStartIndex);
        },
        complete: () => {
          this._handleStreamComplete(streamingStartIndex);
        },
        error: (error) => {
          this._handleStreamError(error, streamingStartIndex);
        },
      });
  }

  public onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendQuery();
    }
  }

  public clearConversation(): void {
    this.messages.length = 0;
    this.sessionId = undefined;
    this._addWelcomeMessage();
  }

  private _addUserMessage(prompt: string): void {
    this.messages.push({
      text: prompt,
      isUser: true,
      timestamp: new Date(),
      status: ZenAiMessageStatus.Complete,
    });
    this._cdr.detectChanges();
    this._scrollToBottomIfNeeded();
  }

  private _createQueryRequest(prompt: string): ZenAIQueryRequest {
    const sourceUrl = this._document.location.pathname + this._document.location.search;
    return {
      domain: this.domain,
      prompt,
      sourceUrl,
      sessionId: this.sessionId,
    };
  }

  private _handleStreamEvent(event: ZenAIStreamEvent, streamingStartIndex: number): void {
    switch (event.type) {
      case ZenAIStreamEventType.Reasoning:
        this._handleReasoningEvent(event);
        break;
      case ZenAIStreamEventType.Complete:
        this._handleCompleteEvent(event, streamingStartIndex);
        break;
      case ZenAIStreamEventType.Error:
        this._handleErrorEvent(event, streamingStartIndex);
        break;
    }
  }

  private _handleReasoningEvent(event: ZenAIReasoningEvent): void {
    // Reasoning messages always show as shimmer boxes (status: Reasoning)
    // They are never cleared until Complete/Error arrives

    if (event.accumulate) {
      // Append to the LAST message (should be a reasoning shimmer box)
      const lastMessage = this.messages.length > 0 ? this.messages[this.messages.length - 1] : null;
      if (lastMessage && lastMessage.status === ZenAiMessageStatus.Reasoning && !lastMessage.isUser) {
        // Append to existing reasoning shimmer box
        lastMessage.text += event.text;
      } else {
        // Edge case: no reasoning box to append to, create new one
        this.messages.push({
          text: event.text,
          isUser: false,
          timestamp: new Date(),
          status: ZenAiMessageStatus.Reasoning,
        });
      }
    } else {
      // Create NEW reasoning shimmer box
      this.messages.push({
        text: event.text,
        isUser: false,
        timestamp: new Date(),
        status: ZenAiMessageStatus.Reasoning,
      });
    }

    this._cdr.detectChanges();
    this._scrollToBottomIfNeeded();
  }

  private _handleCompleteEvent(event: ZenAICompleteEvent, streamingStartIndex: number): void {
    this.isLoading = false;

    if (event.sessionId) {
      this.sessionId = event.sessionId;
    }

    // Remove all shimmer boxes (reasoning messages) and add final complete message
    this.messages.splice(streamingStartIndex, this.messages.length - streamingStartIndex);
    this.messages.push({
      text: event.text,
      isUser: false,
      timestamp: new Date(),
      status: ZenAiMessageStatus.Complete,
      url: event.url,
      formattedText: this._formatTextWithLinks(event.text),
    });

    this._cdr.detectChanges();
    this._scrollToBottomIfNeeded();
    this._logger.debug('ZenAI streaming query completed', { event });
  }

  private _handleErrorEvent(event: ZenAIErrorEvent, streamingStartIndex: number): void {
    this.messages.splice(streamingStartIndex, this.messages.length - streamingStartIndex);
    this.isLoading = false;
    this._logger.error('ZenAI query error', { event });
    this._notificationsService.error(event.message);
    this._addErrorMessage();
    this._cdr.detectChanges();
    this._scrollToBottomIfNeeded();
  }

  private _handleStreamComplete(streamingStartIndex: number): void {
    if (this.isLoading) {
      this.messages.splice(streamingStartIndex, this.messages.length - streamingStartIndex);
      this.isLoading = false;
      this._addErrorMessage();
      this._cdr.detectChanges();
      this._scrollToBottomIfNeeded();
    }
    this._logger.debug('ZenAI stream connection closed');
  }

  private _handleStreamError(error: unknown, streamingStartIndex: number): void {
    if (this.isLoading) {
      this.messages.splice(streamingStartIndex, this.messages.length - streamingStartIndex);
      this.isLoading = false;
      this._logger.error('ZenAI streaming query failed', { error });
      this._notificationsService.error('Connection error. Please try again.');
      this._addErrorMessage();
      this._cdr.detectChanges();
      this._scrollToBottomIfNeeded();
    } else {
      // Stream completed successfully but connection closed with error - this is normal for SSE
      this._logger.debug('ZenAI stream connection closed after successful completion', { error });
    }
  }

  private _getDomainDisplayName(): string {
    switch (this.domain) {
      case ZenAIDomain.Violations:
        return 'violations';
      default:
        return 'items';
    }
  }

  private _getWelcomeMessage(): string {
    return `Hello! I'm ZenAI, your AI assistant. Ask me anything about ${this._getDomainDisplayName()} on this page.`;
  }

  private _addWelcomeMessage(): void {
    const welcomeText = this._getWelcomeMessage();
    this.messages.push({
      text: welcomeText,
      isUser: false,
      timestamp: new Date(),
      status: ZenAiMessageStatus.Complete,
      formattedText: this._formatTextWithLinks(welcomeText),
    });
  }

  private _addErrorMessage(): void {
    const errorText = 'Sorry, I encountered an error. Please try again.';
    this.messages.push({
      text: errorText,
      isUser: false,
      timestamp: new Date(),
      status: ZenAiMessageStatus.Complete,
      formattedText: this._formatTextWithLinks(errorText),
    });
  }

  /**
   * Scrolls to the bottom of the messages container if auto-scroll is enabled
   */
  private _scrollToBottomIfNeeded(): void {
    if (!this._shouldAutoScroll || !this._messagesContainer) {
      return;
    }

    const element = this._messagesContainer.nativeElement;
    const currentScrollHeight = element.scrollHeight;

    // Only scroll if content has changed
    if (currentScrollHeight !== this._lastScrollHeight) {
      this._lastScrollHeight = currentScrollHeight;
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        element.scrollTop = element.scrollHeight;
      }, 0);
    }
  }

  /**
   * Converts markdown text to HTML using MarkdownService
   * Supports all markdown syntax including headings, bold, italic, links, lists, etc.
   */
  private _formatTextWithLinks(text: string): SafeHtml {
    if (!text) {
      return '';
    }

    // Use MarkdownService to parse markdown to HTML
    const parsedResult = this._markdownService.parse(text);

    // Handle both synchronous and asynchronous results
    if (parsedResult instanceof Promise) {
      // For async case (rare), return a fallback with basic HTML formatting
      // In practice, parse() is typically synchronous
      return this._sanitizer.bypassSecurityTrustHtml(text.replace(/\n/g, '<br>'));
    }

    // Parse the markdown to HTML
    let html = parsedResult as string;

    // Post-process links to add target="_blank"
    // This ensures external links open in new tabs
    // Styling is handled by CSS in zen-ai-message.component.scss
    html = html.replace(/<a href="([^"]+)"([^>]*)>/g, (match, url, attrs) => {
      // Check if target="_blank" is already present
      if (attrs && attrs.includes('target=')) {
        return match;
      }
      // Add target="_blank" and rel="noopener noreferrer" if not present
      const hasRel = attrs && attrs.includes('rel=');
      const relAttr = hasRel ? '' : ' rel="noopener noreferrer"';
      return `<a href="${url}" target="_blank"${relAttr}${attrs}>`;
    });

    // Use bypassSecurityTrustHtml - MarkdownService already sanitizes the HTML
    return this._sanitizer.bypassSecurityTrustHtml(html);
  }
}
